import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import heatmapOverlay from '../../assets/images/Mask_Lmdpi.png';
import heatmapBackground from '../../assets/images/sensorInsoles.png';
import { HeatmapData } from '../../model/passive/heatmapData';
import { PassiveParameterType } from '../../model/passive/passiveParameterType';
import Heatmap from '../Heatmap/Heatmap';
import { HeatmapSizeEnum } from '../Heatmap/HeatmapSizeEnum';
import { computeCenterCOP, computeCOP, CopPoint } from '../Heatmap/heatmapFunctions';
import { pressureTransforms } from '../Heatmap/heatmapTransforms';
import { buildPressureTimelineModel } from './pressureTimelineModel';

export interface InsoleDataEntry {
  // Pressure/IMU sample entries carry `semiRawImuPs` + `ts`...
  semiRawImuPs?: {
    ps: number[];
    acc?: { x: number; y: number; z: number };
    gyro?: { x: number; y: number; z: number };
    quat?: number[];
  };
  ts?: number;
  // ...while a single `logmsg` entry per stream correlates an insole `ts` to a
  // UTC datetime, used to put both feet on one clock.
  logmsg?: {
    ts: number;
    string: string;
    raw?: string;
  };
}

export interface InsoleData {
  deviceMask: number;
  fwVersion: number;
  data: InsoleDataEntry[];
}

export interface PressureTimelineData {
  leftInsole: InsoleData;
  rightInsole: InsoleData;
  dateTime?: string;
  activeType?: string;
}

interface PressureTimelineViewerProps {
  data: PressureTimelineData;
}

const HEATMAP_SIZE = 260;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
  },
  heatmapContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  sliderContainer: {
    width: '100%',
    maxWidth: '520px',
    paddingLeft: '8px',
    paddingRight: '8px',
    boxSizing: 'border-box',
  },
  controls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  speedButton: {
    fontSize: '0.7rem !important',
    padding: '0 8px !important',
  },
  toggleLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.8rem',
    },
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  slider: {
    '& .MuiSlider-markLabel': {
      fontSize: '0.65rem',
    },
  },
  warning: {
    maxWidth: '520px',
    fontSize: '0.75rem',
    color: '#8a6d3b',
    backgroundColor: '#fcf8e3',
    border: '1px solid #faebcc',
    borderRadius: '4px',
    padding: '6px 10px',
    textAlign: 'center',
  },
});

const formatRelativeMs = (ms: number): string => {
  if (ms < 1000) return `+${ms}ms`;
  const s = ms / 1000;
  return s % 1 === 0 ? `+${s}s` : `+${s.toFixed(3)}s`;
};

// "Nice" spacings for the timeline's labelled marks, in ms.
const NICE_MARK_STEPS_MS = [
  1000, 2000, 5000, 10000, 15000, 30000, 60000, 120000, 300000, 600000,
];

/**
 * Choose the labelled-mark interval so a long recording doesn't crush its
 * labels together. Always ticking every second means a 17s capture packs ~18
 * labels into the slider and they overlap; instead pick the smallest "nice"
 * step that keeps the label count at or below `maxMarks`.
 */
export const chooseMarkStepMs = (durationMs: number, maxMarks = 8): number => {
  const step = NICE_MARK_STEPS_MS.find((s) => durationMs / s <= maxMarks);
  return step ?? NICE_MARK_STEPS_MS[NICE_MARK_STEPS_MS.length - 1];
};

const PressureTimelineViewer: React.FC<PressureTimelineViewerProps> = ({
  data,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [sampleIndex, setSampleIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [showCop, setShowCop] = useState(false);
  const [showTrace, setShowTrace] = useState(false);

  // Align both feet onto one shared clock (UTC via each insole's logmsg, or
  // relative time as a fallback) so they stay in sync regardless of differing
  // sample counts / independent insole counters.
  const model = useMemo(() => buildPressureTimelineModel(data), [data]);
  const frameCount = model.frames.length;

  // Reset playback when the recording (hence the timeline) changes.
  useEffect(() => {
    setSampleIndex(0);
    setIsPlaying(false);
  }, [model]);

  const sliderMarks = useMemo(() => {
    if (frameCount <= 1) return [];

    const { times, startMs, durationMs } = model;
    const marks: { value: number; label: string }[] = [];
    const stepMs = chooseMarkStepMs(durationMs);

    for (let ms = 0; ms <= durationMs; ms += stepMs) {
      const targetT = startMs + ms;
      // Find the last frame at or before this instant.
      let idx = 0;
      for (let i = 0; i < times.length; i++) {
        if (times[i] <= targetT) idx = i;
        else break;
      }
      marks.push({ value: idx, label: formatRelativeMs(ms) });
    }

    return marks;
  }, [frameCount, model]);

  const currentFrame = model.frames[sampleIndex];

  const heatmapData = useMemo(() => {
    if (!currentFrame) {
      return new HeatmapData('[]', '[]');
    }
    // Render each foot independently so a gap on one side doesn't blank both.
    return new HeatmapData(
      JSON.stringify(currentFrame.leftPs ?? []),
      JSON.stringify(currentFrame.rightPs ?? [])
    );
  }, [currentFrame]);

  const leftCopHistory = useMemo<(CopPoint | null)[]>(
    () => model.frames.map((f) => (f.leftPs ? computeCOP(f.leftPs, false) : null)),
    [model]
  );
  const rightCopHistory = useMemo<(CopPoint | null)[]>(
    () => model.frames.map((f) => (f.rightPs ? computeCOP(f.rightPs, true) : null)),
    [model]
  );
  const centerCopHistory = useMemo<(CopPoint | null)[]>(
    () =>
      model.frames.map((f) =>
        f.leftPs && f.rightPs ? computeCenterCOP(f.leftPs, f.rightPs) : null
      ),
    [model]
  );

  // Shared-clock instant per frame, for the COP trace's time-based trailing window.
  const copTimestamps = model.times;

  useEffect(() => {
    if (!isPlaying) return;
    if (sampleIndex >= frameCount - 1) {
      setIsPlaying(false);
      return;
    }

    const times = model.times;
    const anchorT = times[sampleIndex];
    const wallStart = performance.now();
    let lastEmitted = sampleIndex;
    let raf: number;

    const tick = () => {
      const elapsed = (performance.now() - wallStart) * speed;
      const targetT = anchorT + elapsed;

      let idx = lastEmitted;
      while (idx < frameCount - 1 && times[idx + 1] <= targetT) {
        idx++;
      }

      if (idx >= frameCount - 1) {
        setSampleIndex(frameCount - 1);
        setIsPlaying(false);
        return;
      }

      if (idx !== lastEmitted) {
        lastEmitted = idx;
        setSampleIndex(idx);
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // sampleIndex intentionally excluded: loop anchors to start position, not re-triggered per frame
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, speed, frameCount, model]);

  const handlePlayPause = () => {
    if (sampleIndex >= frameCount - 1) {
      setSampleIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying((prev) => !prev);
    }
  };

  return (
    <div className={classes.root} data-testid="pressure-timeline-viewer">
      <div className={classes.heatmapContainer}>
        <Heatmap
          height={HEATMAP_SIZE}
          width={HEATMAP_SIZE}
          magnitude={HeatmapSizeEnum.Large}
          data={heatmapData}
          type={PassiveParameterType.AlarmPressure}
          background={heatmapBackground}
          overlay={heatmapOverlay}
          transforms={pressureTransforms}
          copLeftHistory={showCop ? leftCopHistory : undefined}
          copRightHistory={showCop ? rightCopHistory : undefined}
          copCenterHistory={showCop ? centerCopHistory : undefined}
          copCurrentIndex={sampleIndex}
          copShowTrace={showCop && showTrace}
          copTimestamps={copTimestamps}
        />
      </div>

      {model.warnings.length > 0 && (
        <div className={classes.warning} role="alert">
          {model.warnings
            .map((code) => t(`pressureTimeline.warnings.${code}`))
            .join(' ')}
        </div>
      )}

      <div className={classes.controls}>
        {frameCount > 1 && (
          <>
            <IconButton onClick={handlePlayPause} size="small" color="primary">
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <ToggleButtonGroup
              value={speed}
              exclusive
              onChange={(_, val) => {
                if (val !== null) setSpeed(val);
              }}
              size="small"
            >
              <ToggleButton value={1} className={classes.speedButton}>1x</ToggleButton>
              <ToggleButton value={0.5} className={classes.speedButton}>0.5x</ToggleButton>
              <ToggleButton value={0.25} className={classes.speedButton}>0.25x</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </div>

      <div className={classes.toggleGroup}>
        <FormControlLabel
          control={
            <Switch
              checked={showCop}
              onChange={(e) => {
                setShowCop(e.target.checked);
                if (!e.target.checked) setShowTrace(false);
              }}
              size="small"
              color="primary"
            />
          }
          label={t('pressureTimeline.cop')}
          className={classes.toggleLabel}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showTrace}
              onChange={(e) => setShowTrace(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={t('pressureTimeline.copTraces')}
          className={classes.toggleLabel}
          style={{ visibility: showCop ? 'visible' : 'hidden' }}
        />
      </div>

      {frameCount > 1 && (
        <div className={classes.sliderContainer}>
          <Slider
            value={sampleIndex}
            min={0}
            max={frameCount - 1}
            step={1}
            marks={sliderMarks}
            onChange={(_, value) => {
              setIsPlaying(false);
              setSampleIndex(value as number);
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              const t = model.times[value] ?? model.startMs;
              return formatRelativeMs(t - model.startMs);
            }}
            className={classes.slider}
            sx={isPlaying ? {
              '& .MuiSlider-thumb': { transition: 'none' },
              '& .MuiSlider-track': { transition: 'none' },
            } : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default PressureTimelineViewer;
